import React, { Component } from 'react';
import { observer } from 'mobx-react';

import { Text } from 'core/components';
import { formConsumer } from 'core/form';
import SelectedValueButtonCompact from 'app/components/device/SelectedValueButtonCompact';
import { BaseAgentRenderer } from 'app/views/synthetics/components/AgentRenderer';

@formConsumer
@observer
class AgentSelectorDisplay extends Component {
  static defaultProps = {
    disabled: false,
    readOnly: false
  };

  get values() {
    const { fieldName, form, multi, useValues, values: valuesProp } = this.props;
    return (useValues ? valuesProp : form.getValue(fieldName)) || (multi ? [] : '');
  }

  get sortedOptions() {
    const { options } = this.props;

    return this.values
      .reduce((acc, agentId) => {
        const agent = options.find((option) => option.value === agentId);
        return agent ? acc.concat(agent) : acc;
      }, [])
      .sort((a, b) => {
        if (a.agent_type === 'private' && b.agent_type === 'global') {
          return -1;
        }

        if (a.agent_type === b.agent_type && a.label.toLowerCase() < b.label.toLowerCase()) {
          return -1;
        }

        return 1;
      });
  }

  handleRemove = (option) => {
    const { onRemove } = this.props;
    onRemove(option);
  };

  isIpMissing(ip) {
    return !ip || ip.length === 0 || ip[0].value === '';
  }

  hasMissingLocalIp(agent) {
    const { agent_family, private_ipv4_addresses, private_ipv6_addresses } = agent;
    if (agent_family === 'v4') {
      return this.isIpMissing(private_ipv4_addresses);
    }
    if (agent_family === 'v6') {
      return this.isIpMissing(private_ipv6_addresses);
    }
    if (agent_family === 'DUAL') {
      return this.isIpMissing(private_ipv4_addresses) && this.isIpMissing(private_ipv6_addresses);
    }
    return false;
  }

  renderAgent(agent, { isLast }) {
    const { form, fieldName, model, readOnly, disabled, relationshipErrors } = this.props;

    if (!agent) {
      // Should we allow them to remove this or just hide this?
      return null;
    }

    const isReciprocal =
      fieldName === 'config.agents' && model && model.get('test_type') === 'agent'
        ? form.getValue('config.reciprocal')
        : null;

    const intentErrors = relationshipErrors
      ? [
          relationshipErrors.localIp && this.hasMissingLocalIp(agent),
          relationshipErrors.ipV6 && agent.agent_family === 'v4',
          relationshipErrors.tcp && (isReciprocal === null || isReciprocal) && agent.agent_type === 'global'
        ]
      : [];

    return (
      <SelectedValueButtonCompact
        key={agent.value}
        option={agent}
        intent={intentErrors.includes(true) ? 'danger' : 'none'}
        onRemove={readOnly ? undefined : this.handleRemove}
        field={form.getField(fieldName)}
        disabled={disabled}
        label={
          <BaseAgentRenderer {...agent} locationInfoProps={{ ellipsis: true }} showLocationInfo showLabels wrapLabels />
        }
        round={false}
        isLast={isLast}
        multiline
      />
    );
  }

  render() {
    const { emptyText, multi, options, showEmptyText, small } = this.props;
    const { values } = this;

    if (values.length === 0 && showEmptyText) {
      return (
        <Text as="div" small={small} muted>
          {emptyText}
        </Text>
      );
    }

    if (multi) {
      const { sortedOptions } = this;
      return sortedOptions.map((value, index) =>
        this.renderAgent(value, { isLast: index === sortedOptions.length - 1 })
      );
    }

    const agent = options.find((option) => option.value === parseInt(values));
    return this.renderAgent(agent, { isLast: false });
  }
}

export default AgentSelectorDisplay;
